import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import styled from 'styled-components';

// components
import { Card, Col, Drawer, FloatButton, Layout, Row, Segmented } from 'antd';
import FullPageLoader from '../components/FullPageLoader';
const Home = lazy(() => import('../views/demo/Home'));
const Session = lazy(() => import('../views/demo/Session'));
const Nudge = lazy(() => import('../views/demo/Nudge'));
const NoPage = lazy(() => import('../views/404'));
const ManagerNudge = lazy(() => import('../views/demo/ManagerNudge'));
const TouchPoint = lazy(() => import('../views/demo/Touchpoint'));
const Header = lazy(() => import('../components/Layout/Header'));
const BehaviourSurvey = lazy(() => import('../views/demo/BehaviourSurvey'));
const DemoSuggestionBox = lazy(() =>
  import('../components/demo/SuggestionBox'),
);
const DemoRecognition = lazy(() => import('../components/demo/Recognition'));
const JourneyCertificate = lazy(() =>
  import('../views/demo/JourneyCertificate'),
);
const FollowUp = lazy(() => import('../views/demo/Loop/FollowUp'));
const ManagerLoop = lazy(() => import('../views/demo/Loop/ManagerLoop'));
const LoopDetails = lazy(() => import('../views/demo/Loop/LoopDetails'));

// utils
import { useSession } from '../Providers/SessionProviders';
import { translate } from '../i18n/i18n';
import useWindowSize from '../hooks/window';

// constants
import {
  APP_ENV,
  DEFAULT_ACTIVE_SEGMENT,
  DEFAULT_ENVIRONMENT,
} from '../constant';
import { FormOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { getDefaultUser } from '../data';

const { Content } = Layout;

const StyledContent = styled(Content)`
  padding: ${(props) => props.padding || '30px'};
  height: calc(100vh - 65px);
  overflow: auto;
  overflow-x: hidden;
`;

const StyledCard = styled(Card)`
  &.ant-card {
    border-radius: 0px;
    // height: calc(100vh - 60px);
  }
  .ant-card-body {
    padding-bottom: 0px;
  }
`;

const StyledSegment = styled(Segmented)`
  .ant-segmented-item-selected {
    background: #ecf5fe;
    border: 1px solid ${(props) => props.theme.colorPrimary};
    border-radius: 4px;
    color: ${(props) => props.theme.colorPrimary};
  }
`;

const Wrapper = styled.div`
  padding-top: 20px;
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 10px;
  }
`;

const t = translate(['common']);

const DemoRouter = () => {
  const {
    currentUser,
    setCurrentUser,
    setIsLoading,
    setLoginLoading,
    setShowHeaderLoading,
  } = useSession();
  const { width } = useWindowSize();
  const { pathname } = useLocation();

  const [activeSegment, setActiveSegment] = useState(DEFAULT_ACTIVE_SEGMENT);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    setCurrentUser(getDefaultUser());
    setIsLoading(false);
    setLoginLoading(false);
    setShowHeaderLoading(false);
  }, []);

  const getBox = useCallback(() => {
    if (APP_ENV === DEFAULT_ENVIRONMENT.DEMO) {
      return activeSegment === 0 ? (
        <DemoSuggestionBox isDrawer={openDrawer} />
      ) : (
        <DemoRecognition isDrawer={openDrawer} />
      );
    }
  }, [openDrawer, activeSegment, currentUser]);

  const getSegmentItems = useCallback(() => {
    const data = [];
    if (currentUser?.company?.suggestion_enabled) {
      data.push({ label: t('suggestionBox'), value: 0 });
    }
    if (currentUser?.company?.recognition_enabled) {
      data.push({ label: t('recognition'), value: 1 });
    }
    return data;
  }, [currentUser]);

  const allRoutes = (
    <Routes>
      <Route path={`/`} element={<Home />} />
      <Route path={`/nudge/:nudgeId`} element={<Nudge />} />
      <Route path={`/session/:sessionId`} element={<Session />} />
      <Route path={`/touchpoint/:sessionId`} element={<TouchPoint />} />
      <Route path={`/manager-nudge`} element={<ManagerNudge />} />
      <Route path={`/behaviour`} element={<BehaviourSurvey />} />
      <Route path={`/alignment`} element={<BehaviourSurvey />} />
      <Route path={`/followup`} element={<FollowUp />} />
      <Route path={`/certificate/123`} element={<JourneyCertificate />} />
      <Route path={`/loop`} element={<ManagerLoop />} />
      <Route path={`/loop/:loopId`} element={<LoopDetails />} />
      <Route path={`*`} element={<NoPage />} />
    </Routes>
  );

  if (width <= 820) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <Layout id="layout">
          <Header />
          <StyledContent
            padding="10px"
            style={
              pathname.startsWith('/certificate')
                ? {
                    overflow: 'auto',
                  }
                : {}
            }>
            {allRoutes}
          </StyledContent>
          {currentUser?.company?.suggestion_enabled && (
            <FloatButton
              icon={<QuestionCircleOutlined />}
              type="primary"
              shape="square"
              style={{
                right: currentUser?.company?.recognition_enabled ? 55 : 10,
              }}
              onClick={() => {
                setOpenDrawer(true);
                setActiveSegment(0);
              }}
            />
          )}
          {currentUser?.company?.recognition_enabled && (
            <FloatButton
              icon={<FormOutlined />}
              type="primary"
              shape="square"
              style={{ right: 10 }}
              // tooltip={t('recognition')}
              onClick={() => {
                setOpenDrawer(true);
                setActiveSegment(1);
              }}
            />
          )}
          {(currentUser?.company?.suggestion_enabled ||
            currentUser?.company?.recognition_enabled) && (
            <StyledDrawer
              title={
                activeSegment === 1 ? t('recognition') : t('suggestionBox')
              }
              width={width}
              onClose={() => {
                setOpenDrawer(false);
                setActiveSegment(0);
              }}
              open={openDrawer}
              destroyOnClose>
              {openDrawer && getBox()}
            </StyledDrawer>
          )}
        </Layout>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Layout id="layout">
        <Header />
        <Row>
          <Col
            span={
              currentUser?.company?.suggestion_enabled ||
              currentUser?.company?.recognition_enabled
                ? 16
                : 24
            }>
            <StyledContent padding="10px">{allRoutes}</StyledContent>
          </Col>
          {(currentUser?.company?.suggestion_enabled ||
            currentUser?.company?.recognition_enabled) && (
            <Col span={8}>
              <StyledCard>
                <StyledSegment
                  block
                  value={activeSegment}
                  options={getSegmentItems()}
                  onChange={(e) => setActiveSegment(e)}
                />
                <Wrapper>{getBox()}</Wrapper>
              </StyledCard>
            </Col>
          )}
        </Row>
      </Layout>
    </Suspense>
  );
};

export default DemoRouter;
